import { useCallback } from 'react';
import { useNotification } from '@/common/components/notification/NotificationContext';
import { useIsElectron, useElectronAPI } from '@/context/ElectronContext';
import { extractMostRecentDAWFile } from '@/common/utils/dawFileUtils';
import { openFile } from '@/common/utils/fileOperations';
import { DAWType } from '@/types/daw';

interface UseOpenProjectInDAWProps {
  projectDirPath: string | null;
  latestVersionFiles: Array<{
    fileName: string;
    uploadedAt: string;
    [key: string]: unknown;
  }>;
  primaryDAW: { type: DAWType } | null;
}

/**
 * Hook to handle opening a project in its DAW
 * Encapsulates the logic for finding and opening the most recent DAW file
 */
export function useOpenProjectInDAW(
  source: UseOpenProjectInDAWProps | (() => Promise<UseOpenProjectInDAWProps>)
) {
  const { showSuccess, showError } = useNotification();
  const isElectron = useIsElectron();
  const electronAPI = useElectronAPI();

  const handleOpenProject = useCallback(async () => {
    try {
      // List rows load their manifest on demand; the project page already has it.
      const { projectDirPath, latestVersionFiles, primaryDAW } =
        typeof source === 'function' ? await source() : source;

      if (!projectDirPath) {
        showError('Project location not set. Please locate the project first.');
        return;
      }

      if (!latestVersionFiles || latestVersionFiles.length === 0) {
        showError('No project files found. Please upload files first.');
        return;
      }

      if (
        !primaryDAW ||
        primaryDAW.type === DAWType.STEMS ||
        primaryDAW.type === DAWType.UNKNOWN
      ) {
        showError('This project has no supported DAW project file to launch.');
        return;
      }

      // Find the most recent primary DAW file from the latest version
      const result = await extractMostRecentDAWFile(
        latestVersionFiles,
        primaryDAW,
        projectDirPath,
        electronAPI,
        isElectron
      );

      if (!result) {
        showError(
          `No ${primaryDAW.type} project file found in the root directory`
        );
        return;
      }

      const { file: dawFile, filePath } = result;

      // Open the file using the unified file opening utility
      const openResult = await openFile(filePath, electronAPI, isElectron);

      if (openResult.success) {
        showSuccess(`Opening ${dawFile.fileName} in ${primaryDAW.type}...`);
      } else {
        showError(openResult.error || 'Failed to open project file');
      }
    } catch (error) {
      console.error('Error opening project:', error);
      showError(
        'Failed to open project. Please ensure the file exists locally and the desktop app is connected.'
      );
    }
  }, [source, electronAPI, isElectron, showError, showSuccess]);

  return { handleOpenProject };
}
